import i18n from "@/i18n";

/**
 * Condition of the specimen under test — its preparation / fabric state.
 *
 * Canonical values are lowercase symbol-like keys; display labels are resolved
 * per context (see `getSpecimenConditionLabels` and the sensitivity variant).
 *
 * Not to be confused with `SampleInfo.condition` (`disturbed | undisturbed`),
 * which describes how the sample *arrived* at the lab. For vane / fall-cone
 * sensitivity work, `undisturbed` is the intact specimen and `remoulded` the
 * deliberately remoulded one (after 5 revolutions).
 */
export type SpecimenCondition = "undisturbed" | "remoulded" | "recompacted";

export const SPECIMEN_CONDITION_VALUES: readonly SpecimenCondition[] = ["undisturbed", "remoulded", "recompacted"];

export const SPECIMEN_CONDITION_VALUES_BASIC: readonly SpecimenCondition[] = ["undisturbed", "remoulded"];

export const getSpecimenConditionLabels = (): Record<SpecimenCondition, string> => ({
  undisturbed: i18n.t("common:specimenCondition.undisturbed"),
  remoulded: i18n.t("common:specimenCondition.remoulded"),
  recompacted: i18n.t("common:specimenCondition.recompacted"),
});

export const getSpecimenConditionSensitivityLabels = (): Partial<Record<SpecimenCondition, string>> => ({
  undisturbed: i18n.t("common:specimenCondition.sensitivity.undisturbed"),
  remoulded: i18n.t("common:specimenCondition.sensitivity.remoulded"),
});

export function normalizeSpecimenCondition(raw?: string | null): SpecimenCondition | null {
  switch ((raw ?? "").trim().toLowerCase()) {
    case "undisturbed":
      return "undisturbed";
    case "remolded":
    case "remoulded":
      return "remoulded";
    case "recompacted":
      return "recompacted";
    default:
      return null;
  }
}
